import React, { useLayoutEffect, useMemo, useRef } from 'react';
import './HarborNav.css';
import './ClassicNav.css';
import { MenuPanel, useMenuPanel } from './MenuPanel';

// The classic look's section bar. It draws the same navItems, selectedKeys and
// navigate() as the Harbor bar, and its menus are the same panel, in the
// classic colours (ClassicNav.css). When the labels do not fit, the keys move
// closer together first; only if that is not enough does the bar show icons only,
// then pack them closer, then scroll.
export default function ClassicNav({ items, selectedKeys, onNavigate }) {
  const reduced = useMemo(() => !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const barRef = useRef(null);
  const panel = useMenuPanel({ barRef, reduced });

  const itemKeys = items.map((i) => i.key).join('|');
  useLayoutEffect(() => {
    const nav = barRef.current;
    const fit = () => {
      nav.classList.remove('hb-snug', 'hb-compact', 'hb-tight');
      const over = () => nav.scrollWidth > nav.clientWidth;
      if (over()) nav.classList.add('hb-snug');
      if (over()) { nav.classList.remove('hb-snug'); nav.classList.add('hb-compact'); }
      if (over()) nav.classList.add('hb-tight');
      // icons only: the name shows on hover
      const compact = nav.classList.contains('hb-compact');
      nav.querySelectorAll('.hb-ckey').forEach((b) => { b.title = compact ? b.getAttribute('aria-label') : ''; });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [itemKeys]);

  return (
    <nav className="hb-cnav" ref={barRef} aria-label="Sections">
      {items.map((item) => {
        const hasMenu = !!(item.children && item.children.length);
        return (
          <button
            key={item.key}
            type="button"
            className={`hb-ckey${selected.has(item.key) ? ' hb-active' : ''}`}
            aria-label={item.label}
            aria-haspopup={hasMenu || undefined}
            aria-expanded={hasMenu ? panel.isOpen(item.key) : undefined}
            onPointerEnter={(e) => { if (hasMenu) panel.hoverOpen(e.currentTarget, item); }}
            onPointerLeave={() => { if (hasMenu) panel.scheduleClose(); }}
            onFocus={(e) => { if (hasMenu && e.currentTarget.matches(':focus-visible')) panel.openMenu(e.currentTarget, item, 'click'); }}
            onClick={(e) => (hasMenu ? panel.clickToggle(e.currentTarget, item) : onNavigate(item.key))}
          >
            {item.icon}
            <span className="hb-ckey-lbl">{item.label}</span>
          </button>
        );
      })}
      <MenuPanel panel={panel} selected={selected} onNavigate={onNavigate} reduced={reduced} className="hb-classic" />
    </nav>
  );
}
